/**
 * Facet dropdown options for the Resellers list, extracted from the page so the
 * option lists can be unit tested without mounting the table.
 *
 * The keys here are HALF of a contract with `Api::V1::ResellersController`'s
 * `filterable(...)` declaration; the other half is pinned by
 * `components/table/config-parity.test.ts` via `RESELLERS_TABLE.facetKeys`, and
 * by
 * `xop-brain/apps/crm-api/test/controllers/concerns/declared_keys_export_test.rb`
 * on the server side.
 */
import type { FacetDef } from "@crm-src/components/table/TableFilterBar";
import type { FacetKeysOf } from "@crm-src/components/table/configs";
import { RESELLERS_TABLE } from "@crm-src/components/table/configs";
import { personLabel, type PersonLike } from "@crm-src/components/table/person-label";
import type { ResellerListFilters } from "@crm-src/lib/resellers-api";

/**
 * The Resellers facet keys, DERIVED from the shared config rather than re-typed
 * — so there is exactly one place a key can be misspelled, and misspelling it
 * there breaks the parity assertions.
 */
export type ResellerFacetKey = FacetKeysOf<typeof RESELLERS_TABLE>;

/**
 * Compile-time proof that every declared facet key is a param
 * `ResellerListFilters` actually carries — i.e. one the API implements. A key
 * that only exists in the config otherwise compiles fine and fails at RUNTIME
 * as a param the server drops.
 */
type FacetKeysAreFilterFields =
  ResellerFacetKey extends keyof ResellerListFilters ? true : never;
const _facetKeysAreFilterFields: FacetKeysAreFilterFields = true;
void _facetKeysAreFilterFields;

interface ResellerLike { country?: string | null }

/**
 * The distinct, non-blank countries across a set of resellers, sorted.
 *
 * `resellers.country` is FREE TEXT with a `"U.S.A"` default — there is no
 * country enum, table or picker anywhere in this app to build options from — so
 * the only honest option list is the values actually stored. Whitespace-only
 * values are dropped (the Location cell already ignores them via its
 * `v && v.trim()` test, so offering one would be an option that filters to
 * rows showing no country), and the surviving values are NOT trimmed or
 * case-folded: the facet sends them back as an exact `country=` match, so a
 * normalised label would be an option matching nothing.
 */
export function resellerCountryOptions(resellers: ResellerLike[]): string[] {
  const seen = new Set<string>();
  for (const reseller of resellers) {
    const country = reseller.country;
    if (country && country.trim().length > 0) seen.add(country);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/**
 * Build the Resellers filter-bar facets.
 *
 * Both option lists come from data the page already has: the user roster, and
 * the FULL reseller list the page still loads for exactly this purpose (see
 * `AdministrationResellersPage`). Deriving the countries from the current PAGE
 * instead would make the options change under the operator as they page — and
 * would hide the very value they need to filter by whenever it is not on
 * screen.
 *
 * Both facets are returned even when their source came back empty
 * (`useUsers()` answers 403 with `[]` for a non-admin): dropping an
 * option-less facet from the rendered controls is `TableFilterBar`'s job, so
 * every list page inherits it. This stays a pure function of its inputs.
 */
export function buildResellerFacetDefs(
  users: PersonLike[],
  resellers: ResellerLike[],
): FacetDef<ResellerFacetKey>[] {
  return [
    {
      key: "salesperson_id",
      label: "Salesperson",
      options: users.map((u) => ({
        value: String(u.id),
        label: personLabel(u),
      })),
    },
    {
      key: "country",
      label: "Country",
      options: resellerCountryOptions(resellers).map((country) => ({
        value: country,
        label: country,
      })),
    },
  ];
}
